import { ChevronRight, History, RotateCw, Trash2, X } from "lucide-react";
import { useState } from "react";
import { discardJob, retryJob, type JobListItem, type JobStatus } from "../api";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PageHeader } from "../components/PageHeader";
import { StatusPill } from "../components/StatusPill";
import { useIsMobile } from "../lib/useIsMobile";

interface Props {
  jobs: JobListItem[];
  onOpen: (id: string) => void;
  onChange?: () => void;
}

const TERMINAL: ReadonlySet<JobStatus> = new Set(["ready", "failed", "incomplete"]);

export function Queue({ jobs, onOpen, onChange }: Props): React.JSX.Element {
  const isMobile = useIsMobile();
  const [showHistory, setShowHistory] = useState(false);

  const live = jobs.filter((j) => !TERMINAL.has(j.status));
  const history = jobs.filter((j) => TERMINAL.has(j.status));

  const visible = showHistory ? jobs : live;
  const sorted = [...visible].sort((a, b) => b.createdAt - a.createdAt);

  return (
    <div>
      <PageHeader
        eyebrow="Render queue"
        title="Live pipeline."
        subtitle="Jobs flow through clip → transcribe → detect end → translate → render → social caption. Click any row to open the preview."
      />

      {sorted.length === 0 ? (
        <EmptyQueue
          historyCount={history.length}
          onShowHistory={history.length > 0 ? () => setShowHistory(true) : undefined}
        />
      ) : (
        <>
          <div
            style={{
              background: "rgba(16,18,27,.72)",
              borderRadius: 18,
              border: "1px solid rgba(255,255,255,.06)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: isMobile ? "none" : "grid",
                gridTemplateColumns: "1fr 160px 220px 100px 110px 40px",
                padding: "14px 22px",
                borderBottom: "1px solid rgba(255,255,255,.06)",
                fontSize: 11,
                fontWeight: 700,
                color: "var(--ql-fg-muted)",
                textTransform: "uppercase",
                letterSpacing: ".06em",
              }}
            >
              <span>Job</span>
              <span>Status</span>
              <span>Pipeline</span>
              <span>Created</span>
              <span></span>
              <span></span>
            </div>
            {sorted.map((j, i) => (
              <QueueRow
                key={j.id}
                job={j}
                isLast={i === sorted.length - 1}
                onOpen={onOpen}
                onChange={onChange}
                isMobile={isMobile}
              />
            ))}
          </div>
          <HistoryToggle
            showing={showHistory}
            historyCount={history.length}
            liveCount={live.length}
            onToggle={() => setShowHistory((s) => !s)}
          />
        </>
      )}
    </div>
  );
}

/**
 * Bottom-of-list pill that flips between "live only" and "all jobs". Hidden
 * when there's no history to reveal — no point cluttering with a no-op.
 */
function HistoryToggle({
  showing,
  historyCount,
  liveCount,
  onToggle,
}: {
  showing: boolean;
  historyCount: number;
  liveCount: number;
  onToggle: () => void;
}): React.JSX.Element | null {
  if (historyCount === 0) return null;
  const label = showing
    ? liveCount > 0
      ? "Hide history — show live only"
      : "Hide history"
    : `Show history (${historyCount})`;
  return (
    <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
      <button
        type="button"
        onClick={onToggle}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 16px",
          borderRadius: 999,
          border: "1px solid rgba(255,255,255,.08)",
          background: "rgba(255,255,255,.02)",
          color: "var(--ql-fg-muted)",
          fontFamily: "inherit",
          fontSize: 12,
          fontWeight: 600,
          cursor: "pointer",
          transition: "all .15s var(--ql-ease)",
        }}
      >
        <History size={14} strokeWidth={1.85} />
        {label}
      </button>
    </div>
  );
}

interface RowProps {
  job: JobListItem;
  isLast: boolean;
  onOpen: (id: string) => void;
  onChange?: () => void;
  isMobile?: boolean;
}

function QueueRow({ job: j, isLast, onOpen, onChange, isMobile }: RowProps): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [pendingWipe, setPendingWipe] = useState<boolean | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const inFlight = j.status !== "ready" && j.status !== "failed" && j.status !== "incomplete";
  const canHardDiscard = !inFlight; // only wipe when it's safe (not actively writing files)

  const handleDiscard = (e: React.MouseEvent, wipe: boolean): void => {
    e.stopPropagation();
    if (busy) return;
    setPendingWipe(wipe);
  };

  const confirmDiscard = async (): Promise<void> => {
    if (pendingWipe === null) return;
    const wipe = pendingWipe;
    setBusy(true);
    try {
      await discardJob(j.id, wipe);
      setPendingWipe(null);
      onChange?.();
    } catch (err) {
      setPendingWipe(null);
      setErrMsg(`Failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };

  const handleRetry = async (e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      await retryJob(j.id);
      onChange?.();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Legacy jobs (created before input.json persistence) can't auto-retry —
      // open the Job Detail screen so the user can use Re-cut to provide a
      // new start instead of just dead-ending on an alert.
      if (msg.includes("no input.json")) {
        onOpen(j.id);
      } else {
        setErrMsg(`Retry failed: ${msg}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <div
      onClick={() => onOpen(j.id)}
      style={{
        display: isMobile ? "flex" : "grid",
        gridTemplateColumns: isMobile ? undefined : "1fr 160px 220px 100px 110px 40px",
        flexDirection: isMobile ? "column" : undefined,
        padding: isMobile ? "14px 16px" : "16px 22px",
        gap: isMobile ? 10 : undefined,
        alignItems: isMobile ? undefined : "center",
        borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,.04)",
        cursor: "pointer",
        transition: "background .15s",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,.02)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      {/* Job title + ID — always shown */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        <div
          style={{
            width: 32,
            height: 56,
            borderRadius: 6,
            background: thumbForStatus(j.status),
            flexShrink: 0,
          }}
        />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {j.title}
          </div>
          <div
            style={{
              fontSize: 11,
              color: "var(--ql-fg-muted)",
              fontFamily: "var(--ql-font-mono)",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {j.id}
            {j.speaker ? ` · ${j.speaker}` : ""}
            {j.input?.finePan ? (
              <span style={{ color: "var(--ql-yellow)" }}> · fine pan</span>
            ) : null}
          </div>
          {/* On mobile: show status pill inline under the title */}
          {isMobile && (
            <div style={{ marginTop: 6 }}>
              <StatusPill status={j.status} progress={j.progress} />
            </div>
          )}
        </div>
        {/* On mobile: chevron + actions in the top-right of the title row */}
        {isMobile && (
          <div style={{ display: "flex", gap: 6, flexShrink: 0, alignItems: "center" }}>
            {inFlight && (
              <button
                onClick={(e) => handleDiscard(e, false)}
                disabled={busy}
                title="Cancel — kill the running pipeline (files are kept)"
                style={iconActionStyle("#FACC15")}
              >
                <X size={14} strokeWidth={2.5} />
              </button>
            )}
            {canHardDiscard && (
              <button
                onClick={handleRetry}
                disabled={busy}
                title="Retry — re-run the pipeline with the original parameters"
                style={iconActionStyle("#4ADE80")}
              >
                <RotateCw size={14} strokeWidth={2} />
              </button>
            )}
            {canHardDiscard && (
              <button
                onClick={(e) => handleDiscard(e, true)}
                disabled={busy}
                title="Discard — delete work folder, raw clip, rendered video"
                style={iconActionStyle("#F87171")}
              >
                <Trash2 size={14} strokeWidth={2} />
              </button>
            )}
            <div style={{ color: "var(--ql-fg-subtle)" }}>
              <ChevronRight size={16} strokeWidth={2} />
            </div>
          </div>
        )}
      </div>

      {/* Status pill — desktop only (mobile shows it above) */}
      {!isMobile && (
        <div>
          <StatusPill status={j.status} progress={j.progress} />
        </div>
      )}

      {/* Progress bar — always shown, stacks nicely on mobile */}
      <div>
        <div
          style={{
            height: 6,
            background: "rgba(255,255,255,.06)",
            borderRadius: 999,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.max(0, Math.min(1, j.progress)) * 100}%`,
              background:
                j.status === "failed"
                  ? "linear-gradient(90deg,#F87171,#EF4444)"
                  : j.status === "ready"
                  ? "linear-gradient(90deg,#4ADE80,#22C55E)"
                  : j.status === "incomplete"
                  ? "linear-gradient(90deg,#FACC15,#EAB308)"
                  : "linear-gradient(90deg,#FFD500,#FFA500)",
              boxShadow: j.status !== "failed" ? "0 0 8px rgba(255,213,0,.5)" : "none",
              transition: "width .3s",
            }}
          />
        </div>
        <div
          style={{
            fontSize: 10,
            color: "var(--ql-fg-muted)",
            marginTop: 4,
            fontFamily: "var(--ql-font-mono)",
          }}
        >
          {Math.round(j.progress * 100)}%
        </div>
      </div>

      {/* Created timestamp — always shown */}
      <div
        style={{
          fontFamily: "var(--ql-font-mono)",
          fontSize: 12,
          color: "var(--ql-fg-muted)",
        }}
      >
        {timeAgo(j.createdAt)}
      </div>

      {/* Desktop-only action buttons + chevron */}
      {!isMobile && (
        <div style={{ display: "flex", gap: 6 }}>
          {inFlight && (
            <button
              onClick={(e) => handleDiscard(e, false)}
              disabled={busy}
              title="Cancel — kill the running pipeline (files are kept)"
              style={iconActionStyle("#FACC15")}
            >
              <X size={14} strokeWidth={2.5} />
            </button>
          )}
          {canHardDiscard && (
            <button
              onClick={handleRetry}
              disabled={busy}
              title="Retry — re-run the pipeline with the original parameters"
              style={iconActionStyle("#4ADE80")}
            >
              <RotateCw size={14} strokeWidth={2} />
            </button>
          )}
          {canHardDiscard && (
            <button
              onClick={(e) => handleDiscard(e, true)}
              disabled={busy}
              title="Discard — delete work folder, raw clip, rendered video"
              style={iconActionStyle("#F87171")}
            >
              <Trash2 size={14} strokeWidth={2} />
            </button>
          )}
        </div>
      )}
      {!isMobile && (
        <div style={{ color: "var(--ql-fg-subtle)" }}>
          <ChevronRight size={16} strokeWidth={2} />
        </div>
      )}
    </div>
    <ConfirmDialog
      open={pendingWipe !== null}
      title={pendingWipe ? "Discard this clip?" : "Cancel this job?"}
      message={
        pendingWipe
          ? `"${j.title}" will be removed. Work folder, raw clip, and any rendered video will be deleted. This cannot be undone.`
          : `"${j.title}" will stop. Files already on disk are kept — you can retry later.`
      }
      confirmLabel={pendingWipe ? "Delete" : "Cancel job"}
      cancelLabel="Keep"
      danger={pendingWipe === true}
      busy={busy}
      onConfirm={confirmDiscard}
      onCancel={() => !busy && setPendingWipe(null)}
    />
    <ConfirmDialog
      open={errMsg !== null}
      title="Something went wrong"
      message={errMsg ?? ""}
      confirmLabel="Got it"
      cancelLabel=""
      danger={false}
      onConfirm={() => setErrMsg(null)}
      onCancel={() => setErrMsg(null)}
    />
    </>
  );
}

function iconActionStyle(color: string): React.CSSProperties {
  return {
    width: 28,
    height: 28,
    borderRadius: 8,
    border: `1px solid ${color}33`,
    background: `${color}10`,
    color,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: "inherit",
  };
}

function EmptyQueue({
  historyCount,
  onShowHistory,
}: {
  historyCount: number;
  onShowHistory?: () => void;
}): React.JSX.Element {
  return (
    <div
      style={{
        background: "rgba(16,18,27,.72)",
        border: "1px dashed rgba(255,255,255,.10)",
        borderRadius: 18,
        padding: 60,
        textAlign: "center",
        color: "var(--ql-fg-muted)",
      }}
    >
      <div style={{ fontSize: 16, fontWeight: 600, color: "var(--ql-fg)", marginBottom: 6 }}>Queue is idle.</div>
      <div style={{ fontSize: 13 }}>Submit a YouTube URL from the New job screen to kick off a render.</div>
      {onShowHistory && (
        <button
          type="button"
          onClick={onShowHistory}
          style={{
            marginTop: 18,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 16px",
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,.08)",
            background: "rgba(255,255,255,.02)",
            color: "var(--ql-fg-muted)",
            fontFamily: "inherit",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <History size={14} strokeWidth={1.85} />
          Show history ({historyCount})
        </button>
      )}
    </div>
  );
}

function thumbForStatus(status: string): string {
  if (status === "failed") return "linear-gradient(135deg,#3a0d0d 0%,#0a0a0b 100%)";
  if (status === "ready") return "linear-gradient(135deg,#1a3320 0%,#0a0a0b 100%)";
  return "linear-gradient(135deg,#1a1f2e 0%,#0a0a0b 60%,#1f1815 100%)";
}

function timeAgo(ts: number): string {
  const sec = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}
